import React, { useRef, useState, Suspense, useEffect }  from 'react';
import {
  ZapparCamera, ImageTracker, ZapparCanvas, BrowserCompatibility
} from '@zappar/zappar-react-three-fiber';
import { Button} from '@mui/material'

import * as THREE from 'three';
// import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { useLoader, Vector3 } from '@react-three/fiber';
import { Circle, Text } from '@react-three/drei';

import cardBgTextureImg from './assets/HabitTracker.png';

import checkIcon from './assets/checkIcon.png';
import defaultMemberPhoto from './assets/default-avatar.png';
import arrowPhoto from './assets/arrow-right.png';
import ConfigDialog from './configDialog';

import {
  getDoc,
  doc,
  collection,
  addDoc,
  onSnapshot,
  query,
  setDoc,
} from 'firebase/firestore';
import { db } from './firebase';
import ConfigGoalDialog from './configGoalDialog';

const targetFile = new URL('./assets/tracking-image.zpt', import.meta.url).href;
const zapLaserMp3 = new URL('./assets/zapsplat_laser.mp3', import.meta.url).href;

// Background
function Background() {
  const mesh:React.MutableRefObject<any> = useRef();
  const cardBgTexture = useLoader(THREE.TextureLoader, cardBgTextureImg) as THREE.Texture;

  return (
    <mesh ref={mesh} position={[0, 0, 0]}>
      <planeBufferGeometry args={[3, 2]} />
      <meshStandardMaterial attach="material" map={cardBgTexture} />
    </mesh>
  );
}

function Sound() {
  const listener = new THREE.AudioListener();
  const ZapLaser = new THREE.Audio(listener);

  const audioLoader = new THREE.AudioLoader();
  audioLoader.load(zapLaserMp3, (buffer) => {
    ZapLaser.setBuffer(buffer);
    ZapLaser.setLoop(false);
    ZapLaser.play();
  });
}

// Date info
function DateInfo() {
  const date = new Date();
  return (
    <group>
      <mesh>
        <Text color="black" position={[-1.0, 0.7, 0.1]} fontSize={0.08}>
          {date.toLocaleDateString()}
        </Text>
      </mesh>
    </group>
  );
}

// Group or user info
function TextInfo(props: {text: string, position: Vector3}) {
  return (
    <group>
      <mesh>
        <Text color="black" position={props.position} fontSize={0.08}>
          {props.text}
        </Text>
      </mesh>
    </group>
  );
}

// Name and Goal
function UserInfo(props: {member: Member, yPosition: number, isCurrentUser: boolean}) {
  const GAP_Y = 0.1;

  const photo = props.member.photo ? props.member.photo : defaultMemberPhoto;

  const avatarSizeRadius = 0.15;
  const arrowSize = 0.15;
  const userImgTexture = useLoader(THREE.TextureLoader, photo) as THREE.Texture;
  const arrowTexture = useLoader(THREE.TextureLoader, arrowPhoto) as THREE.Texture;

  return (
    <group>
      <mesh>
        <Text color="black" position={[-1.0, props.yPosition, 0.1]} fontSize={0.1}>
          {props.member.name}
        </Text>
        <Text color="black" position={[-1.0, props.yPosition-GAP_Y, 0.1]}
          fontSize={0.05} maxWidth={0.3}>
          {props.member.goal}
        </Text>
      </mesh>
      <mesh position={[-0.65, props.yPosition-0.05, 0.05]}>
        <Circle args={[avatarSizeRadius, 20]}>
          <meshBasicMaterial attach="material" map={userImgTexture} color="white" transparent />
        </Circle>
      </mesh>
      { props.isCurrentUser &&
        <mesh position={[-1.30, props.yPosition-0.05, 0.05]}>
          <planeBufferGeometry args={[arrowSize, arrowSize]} />
          <meshBasicMaterial attach="material" map={arrowTexture} color="white" transparent />
      </mesh>
      }
    </group>
  );
}
function UserChecks(props: {member: Member, yPosition: number,
  isCurrentUser: boolean, onCheckClick: (member: Member, currentChecks: number) => void}) {

  const [hasCheckedToday, setHasCheckedToday] = useState(false);

  const checkTexture1 = useLoader(THREE.TextureLoader, checkIcon) as THREE.Texture;
  const CHECK_SIZE = 0.2;

  const START_X = -0.35;
  const GAP_X = 0.2;

  let x_position = START_X+(0*GAP_X);

  const onCheckedToday = () => {
    setHasCheckedToday(true);
    Sound();
    props.onCheckClick(props.member, props.member.daysChecked);
  }

  return (
    <group>
      { Array.from(Array(props.member.daysChecked).keys()).map((k,idx) => {
        x_position = START_X+(idx*GAP_X);
        return (
          <mesh key={`check-${idx}`}
            position={[x_position, props.yPosition, 0.1]}>
            <planeBufferGeometry args={[CHECK_SIZE, CHECK_SIZE]} />
            <meshBasicMaterial attach="material" map={checkTexture1} color="white" transparent />
          </mesh>
      )
      })}
      { (props.isCurrentUser && !hasCheckedToday) &&
      <mesh>
        <Text color={hasCheckedToday ? 'pink' : 'black'}
          position={[props.member.daysChecked ? x_position+GAP_X : x_position, props.yPosition, 0.1]}
          fontSize={hasCheckedToday ? 1.2 : 0.08}
          onClick={() => onCheckedToday()}>
          Done!
        </Text>
      </mesh>
      }
    </group>
  );
}

function ZapparView(props: {groupInfo?: HabitGroup, members?: Member[], currentMember?: Member, leaderMember?: Member,
    onCheckClick: (member: Member, currentChecks: number) => void}) {
    // Set up states
    const [visibleState, setVisibleState] = useState(false);

    return (
      <>
        <BrowserCompatibility />
        {/* Setup Zappar Canvas */}
        <ZapparCanvas gl={{ antialias: true }} style={{borderRadius: 10}}>
          {/* Setup Zappar Camera */}
          <ZapparCamera matrixWorldAutoUpdate={false}/>
          {/* Setup Zappar Image Tracker, passing our target file */}
          <Suspense fallback={null}>
            <ImageTracker
              onNotVisible={() => { setVisibleState(false); } }
              onNewAnchor={(anchor) => console.log(`New anchor ${anchor.id}`)}
              onVisible={() => { setVisibleState(true); } }
              targetImage={targetFile}
              visible={visibleState}
              matrixWorldAutoUpdate={false}>

              {/* Setup Content */}
              <Background />

              {props.members && props.members.map((member, idx) =>
                <UserInfo member={member} yPosition={0.4-(idx*0.32)} key={`user-info-${idx}`}
                  isCurrentUser={(props.currentMember && props.currentMember.docId === member.docId) ? true : false}/>
              )}

              {props.members && props.members.map((member, idx) =>
                <UserChecks member={member} yPosition={0.35-(idx*0.32)} key={`checks-${idx}`}
                  isCurrentUser={(props.currentMember && props.currentMember.docId === member.docId) ? true : false}
                  onCheckClick={props.onCheckClick}/>
              )}
              <DateInfo />
              {props.groupInfo &&
                <TextInfo text={props.groupInfo.name} position={[-1.0, 0.6, 0.1]} />
              }
              {props.leaderMember &&
                <TextInfo text={props.leaderMember.name} position={[-0.9, -0.7, 0.1]} />
              }

            </ImageTracker>
          </Suspense>
          {/* Normal directional light */}
          <directionalLight position={[2.5, 8, 5]} intensity={1.5} castShadow />
        </ZapparCanvas>
      </>
    );
}

export type Member = {
  docId: string;
  name: string;
  goal: string;
  daysChecked: number;
  photo?: string;
}

type HabitGroup = {
  name: string;
  docId: string;
}

function App() {

  const [habitGroup, setHabitGroup] = useState<HabitGroup>();
  const [members, setMembers] = useState<Member[]>();
  const [currentMember, setCurrentMember] = useState<Member>();
  const [leaderMember, setLeaderMember] = useState<Member>();

  const [configOpen, setConfigOpen] = useState(false);
  const [memberConfigOpen, setMemberConfigOpen] = useState(false);

  useEffect(() => {

    if (!habitGroup?.docId) {
      return;
    }

    const q = query(collection(db, `trackers/${habitGroup?.docId}/members`));
    onSnapshot(q, (qSnapshot) => {

        const members = qSnapshot.docs.map((doc) => {
          const data = doc.data();
          const nodeData: Member = {
            docId: doc.id,
            name: data.name,
            goal: data.goal,
            daysChecked: data.daysChecked,
            photo: data.photo,
          }
          return nodeData;
        })

        setMembers(members);

        if (members.length > 0)
        {
          const leader = members.sort((a,b) => b.daysChecked - a.daysChecked);
          setLeaderMember(leader[0]);
        }
    });
  }, [habitGroup]);

  const onConfigure = () => {
    setConfigOpen(true);
  }

  const createNewGroup = async (groupName: string) => {
    setConfigOpen(false);

    const collectionRef = collection(db, 'trackers');
    const ref = await addDoc(collectionRef, {
      name: groupName,
      members: [],
    });

    const groupRef = doc(db, `trackers/${ref.id}`);
    initGroup(groupRef.id);
  }

  const onConfigureClose = async (groupCode: string) => {
    setConfigOpen(false);
    initGroup(groupCode);
  }

  const onMemberConfigureClose = async (memberData?: {
    name: string,
    goal: string,
    photo: string,
  }) => {

    setMemberConfigOpen(false);

    if (!memberData || !habitGroup?.docId) return;

    const collectionRef = collection(db, 'trackers', habitGroup?.docId, 'members');
    const ref = await addDoc(collectionRef, {
      name: memberData.name,
      goal: memberData.goal,
      photo: memberData.photo,
      daysChecked: 0,
    });

    const userRef = doc(db, `trackers/${habitGroup?.docId}/members/${ref.id}`);
    initMember(userRef.id);
  }

  const initGroup = async (docId: string) => {

    const groupRef = doc(db, `trackers/${docId}`);
    const groupData = (await getDoc(groupRef)).data();
    if (groupData) {
          const nodeData: HabitGroup = {
            name: groupData.name,
            docId: docId,
          }
        setHabitGroup(nodeData);
    };
  }

  const initMember = async (docId: string) => {

    const memberRef = doc(db, `trackers/${habitGroup?.docId}/members/${docId}`);
    const memberData = (await getDoc(memberRef)).data();
    if (memberData) {
          const nodeData: Member = {
            name: memberData.name,
            docId: docId,
            goal: memberData.goal,
            daysChecked: memberData.daysChecked,
            photo: memberData.photo,
          }
        setCurrentMember(nodeData);
    };
  }

  const chooseUser = async (member: Member) => {
    setMemberConfigOpen(false);
    setCurrentMember(member);
  }

  const onCheckClick = async (member: Member, currentChecks: number) => {

    if (!habitGroup?.docId) return;

    if (member.docId === currentMember?.docId) {
      const docRef = doc(db, 'trackers', habitGroup?.docId, 'members', member.docId);
      await setDoc(docRef, {
        daysChecked: currentChecks + 1,
      }, {merge: true});

    } else {
      console.log("was not current member")
    }
  }

  return (
    <div style={{width: '100%',
      height: '100%', display: 'flex', justifyContent: 'center', flexDirection: 'column'}}>
      <div style={{background: 'white', margin: '10px 20px 0px 20px', height: '20%',
        borderRadius: 10, padding: 10}}>
        <h3>Habit tracker {habitGroup && <span>- Group: {habitGroup?.name}</span>} </h3>
        {habitGroup && <p>Group code: {habitGroup?.docId}</p>}

        {habitGroup &&
        <Button onClick={() => setMemberConfigOpen(true)} variant="outlined">
          User settings
        </Button>
        }

        {!habitGroup &&
        <Button onClick={onConfigure} variant="outlined">
          Group settings
        </Button>
        }
      </div>
      <div style={{padding: 20, height: '80%'}}>
        <ZapparView groupInfo={habitGroup} members={members}
          currentMember={currentMember} leaderMember={leaderMember} onCheckClick={onCheckClick}/>
      </div>

      <ConfigDialog open={configOpen} handleClose={onConfigureClose} createNewGroup={createNewGroup}/>
      <ConfigGoalDialog
        open={memberConfigOpen}
        handleClose={onMemberConfigureClose}
        chooseUser={chooseUser}
        currentMember={currentMember}
        allMembers={members}/>
    </div>
  );
}

export default App;
